/**
 * lib/sync/onlineReconciler.ts — ESCALA 0B Fase C (contexto y red).
 *
 * Codex: el evento `online` disparaba una full_reconciliation por CADA evento, sin
 * debounce ni deduplicación → una conexión inestable (flapping) generaba una TORMENTA
 * de reconciliaciones completas concurrentes. Este reconciliador amortigua:
 *
 *   • DEBOUNCE: espera un margen tras el último `online` antes de correr (coalesce del
 *     flapping en una sola corrida).
 *   • DEDUP/COALESCE: mientras hay una corrida agendada o en curso, nuevos `online` NO
 *     agendan otra (una corrida efectiva por ráfaga).
 *   • COOLDOWN + BACKOFF: tras correr, respeta un intervalo mínimo; si los disparos
 *     siguen llegando en ráfaga, el backoff crece exponencialmente hasta un tope y se
 *     reinicia tras un periodo de calma.
 *   • JITTER: reparte el arranque para no sincronizar tormentas entre pestañas.
 *   • Respeta isOnline() e isActive() (contexto vigente) antes de correr.
 *
 * La lógica de timing es pura e inyectable (setTimer/clearTimer/now/jitter) para poder
 * probarla con relojes falsos sin depender del DOM.
 */
export interface OnlineReconcilerOptions {
  /** Corrida real (p.ej. pullFreshData(ctx, 'full_reconciliation')). */
  run: () => Promise<void>
  /** ¿Hay conexión? (navigator.onLine). */
  isOnline: () => boolean
  /** ¿El contexto sigue vigente? (worker no desechado / mismo scope). */
  isActive: () => boolean
  debounceMs?: number
  cooldownMs?: number
  maxBackoffMs?: number
  jitter?: () => number
  setTimer?: (cb: () => void, ms: number) => ReturnType<typeof setTimeout>
  clearTimer?: (h: ReturnType<typeof setTimeout>) => void
  now?: () => number
}

export interface OnlineReconciler {
  /** Llamar en cada evento `online`. */
  trigger: () => void
  /** Cancela cualquier corrida agendada (al desechar el worker). */
  dispose: () => void
}

const DEFAULT_DEBOUNCE_MS = 2_000
const DEFAULT_COOLDOWN_MS = 15_000
const DEFAULT_MAX_BACKOFF_MS = 5 * 60_000

export function createOnlineReconciler(opts: OnlineReconcilerOptions): OnlineReconciler {
  const debounceMs = opts.debounceMs ?? DEFAULT_DEBOUNCE_MS
  const cooldownMs = opts.cooldownMs ?? DEFAULT_COOLDOWN_MS
  const maxBackoffMs = opts.maxBackoffMs ?? DEFAULT_MAX_BACKOFF_MS
  const jitter = opts.jitter ?? (() => Math.floor(Math.random() * 1_000))
  const setTimer = opts.setTimer ?? ((cb, ms) => setTimeout(cb, ms))
  const clearTimer = opts.clearTimer ?? (h => clearTimeout(h))
  const now = opts.now ?? (() => Date.now())

  let timer: ReturnType<typeof setTimeout> | null = null
  let running = false
  let disposed = false
  let lastRunAt = -Infinity // primer disparo: sin cooldown pendiente ni backoff previo
  let backoffMs = cooldownMs // crece en ráfaga, se reinicia tras calma

  const trigger = (): void => {
    if (disposed) return
    if (!opts.isOnline() || !opts.isActive()) return
    // DEDUP/COALESCE: ya hay una corrida agendada o en curso → no apilar otra.
    if (timer !== null || running) return

    // BACKOFF: si el último run fue reciente, se reinició el backoff; si venimos de una
    // ráfaga, respeta el cooldown vigente. El retraso base es el debounce; nunca menor
    // que lo que resta del cooldown desde el último run.
    const sinceLast = now() - lastRunAt
    const cooldownRemaining = Math.max(0, backoffMs - sinceLast)
    const delay = Math.max(debounceMs, cooldownRemaining) + jitter()

    timer = setTimer(() => {
      timer = null
      if (disposed || !opts.isOnline() || !opts.isActive() || running) return
      running = true
      void Promise.resolve()
        .then(opts.run)
        .catch(() => {})
        .finally(() => {
          running = false
          const t = now()
          // BACKOFF exponencial si el gap entre corridas fue corto (flapping); si hubo
          // calma (gap > backoff previo), reinicia al cooldown base.
          if (t - lastRunAt < backoffMs * 2) {
            backoffMs = Math.min(maxBackoffMs, backoffMs * 2)
          } else {
            backoffMs = cooldownMs
          }
          lastRunAt = t
        })
    }, delay)
  }

  const dispose = (): void => {
    disposed = true
    if (timer !== null) {
      clearTimer(timer)
      timer = null
    }
  }

  return { trigger, dispose }
}
